import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { getDdm, getFilters } from './request/request';
import { DdmData, DemandItem, algoColor, mbpsColor } from './Srv6Types';
import InlineSpinner from './InlineSpinner';
import ErrorBanner from './ErrorBanner';
import Tooltip from '../Topology/components/Tooltip';

interface Props {
    timeRange: string;
    onViewPath?: (source: string, dest: string) => void;
    activePair?: { source: string; target: string } | null;
}

const MIN_MBPS_OPTIONS = [0, 0.1, 1, 5, 10];

const COL_TIPS: Record<string, string> = {
    SOURCE: 'Ingress router where SRv6 traffic enters the domain.',
    DEST: 'Egress router where traffic exits toward the destination locator.',
    ALGO: 'Flex-algo (forwarding policy) associated with the locator.',
    'INTERFACE OUT': 'Egress interface on the source router.',
    MBPS: 'Current throughput in megabits per second (derivative rate).',
    GBPS: 'Current throughput in gigabits per second.',
    PPS: 'Packets per second.',
};

const Srv6DemandMatrixTab: React.FC<Props> = ({ timeRange, onViewPath, activePair }) => {
    const { getAccessTokenSilently } = useAuth0();
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [ddmData, setDdmData] = useState<DdmData | null>(null);
    const [lastFetched, setLastFetched] = useState<Date | null>(null);

    const [minMbps, setMinMbps] = useState(0);
    const [algoFilter, setAlgoFilter] = useState('');
    const [algorithms, setAlgorithms] = useState<{ value: string; label: string }[]>([]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const token = await getAccessTokenSilently();
                const res = await getFilters(token);
                if (cancelled) return;
                if (res.data?.data?.algorithms) setAlgorithms(res.data.data.algorithms);
            } catch { /* non-critical */ }
        })();
        return () => { cancelled = true; };
    }, [getAccessTokenSilently]);

    const load = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const token = await getAccessTokenSilently();
            const params: Record<string, string> = { timeRange };
            if (algoFilter) params.algo = algoFilter;
            const res = await getDdm(token, params);
            if (res.data?.data) {
                setDdmData(res.data.data);
                setLastFetched(new Date());
            }
        } catch (err: unknown) {
            setError(err instanceof Error ? err.message : 'Failed to load DDM');
        } finally {
            setIsLoading(false);
        }
    }, [getAccessTokenSilently, timeRange, algoFilter]);

    useEffect(() => { load(); }, [load]);

    const filtered = useMemo<DemandItem[]>(() => {
        if (!ddmData) return [];
        return ddmData.demands.filter((d) => d.mbps >= minMbps);
    }, [ddmData, minMbps]);

    if (isLoading) return <InlineSpinner />;
    if (error) return <ErrorBanner message={error} onRetry={load} />;

    return (
        <div className="flex flex-col gap-4 overflow-auto">
            <p className="text-[11px] text-semantic-text-secondary leading-relaxed">
                Aggregate SRv6 traffic flows: each row is a demand entering at source and exiting at destination, per locator.
            </p>
            <div className="flex items-center gap-3 flex-wrap text-xs text-semantic-text-secondary">
                <Tooltip text="Filter demands below this throughput threshold." position="bottom">
                    <label className="flex items-center gap-1.5">
                        Min traffic
                        <select
                            value={minMbps}
                            onChange={(e) => setMinMbps(Number(e.target.value))}
                            className="px-2 py-1 border rounded-lg bg-semantic-bg-primary
                                       text-semantic-text-primary border-semantic-border-secondary text-xs"
                        >
                            {MIN_MBPS_OPTIONS.map((v) => (
                                <option key={v} value={v}>{v === 0 ? 'All' : `${v} Mbps`}</option>
                            ))}
                        </select>
                    </label>
                </Tooltip>
                <Tooltip text="Filter by flex-algo. Backend filters before returning results." position="bottom">
                    <label className="flex items-center gap-1.5">
                        Algo
                        <select
                            value={algoFilter}
                            onChange={(e) => setAlgoFilter(e.target.value)}
                            className="px-2 py-1 border rounded-lg bg-semantic-bg-primary
                                       text-semantic-text-primary border-semantic-border-secondary text-xs"
                        >
                            <option value="">All</option>
                            {algorithms.map((a) => (
                                <option key={a.value} value={a.value}>{a.label}</option>
                            ))}
                        </select>
                    </label>
                </Tooltip>
                <span className="ml-auto flex items-center gap-2">
                    {ddmData && (
                        <span>
                            Showing <span className="font-semibold text-semantic-text-primary">{filtered.length}</span>
                            {filtered.length !== ddmData.activeDemands && ` of ${ddmData.activeDemands}`} demands
                        </span>
                    )}
                    {lastFetched && (
                        <span className="text-semantic-text-secondary/60">
                            Updated {lastFetched.toLocaleTimeString()}
                        </span>
                    )}
                    <button
                        onClick={load}
                        className="p-1 rounded hover:bg-semantic-bg-secondary transition-colors"
                        title="Refresh"
                    >
                        <svg className="w-3.5 h-3.5" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M1 4v4h4" /><path d="M15 12v-4h-4" />
                            <path d="M13.5 6.5A6 6 0 0 0 3 4.5L1 8m14 0l-2 3.5a6 6 0 0 1-10.5-2" />
                        </svg>
                    </button>
                </span>
            </div>
            <div className="overflow-auto">
                <table className="w-full text-xs border-collapse">
                    <thead>
                        <tr className="border-b border-semantic-border-secondary bg-semantic-bg-secondary">
                            {['SOURCE', 'DEST', 'ALGO', 'INTERFACE OUT'].map((col) => (
                                <th key={col} className="text-left py-2 px-3">
                                    <Tooltip text={COL_TIPS[col] || col} position="bottom">
                                        <span>{col}</span>
                                    </Tooltip>
                                </th>
                            ))}
                            {['MBPS', 'GBPS', 'PPS'].map((col) => (
                                <th key={col} className="text-right py-2 px-3">
                                    <Tooltip text={COL_TIPS[col] || col} position="bottom">
                                        <span>{col}</span>
                                    </Tooltip>
                                </th>
                            ))}
                            <th className="text-center py-2 px-3">PATH</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filtered.map((d) => {
                            const isActive = !!activePair
                                && d.source.toLowerCase() === activePair.source.toLowerCase()
                                && d.dest.toLowerCase() === activePair.target.toLowerCase();
                            return (
                            <tr
                                key={`${d.source}-${d.dest}-${d.algo}-${d.interfaceOut}`}
                                className={`border-b border-semantic-border-secondary/50 transition-colors ${
                                    isActive
                                        ? 'bg-utility-brand-fuchsia-500/10'
                                        : 'hover:bg-semantic-bg-secondary/50'
                                }`}
                                style={isActive ? { boxShadow: 'inset 3px 0 0 #ff4089' } : undefined}
                            >
                                <td className="py-2 px-3 font-mono">{d.source}</td>
                                <td className="py-2 px-3 font-mono">{d.dest}</td>
                                <td className="py-2 px-3">
                                    <span
                                        className="inline-block w-2 h-2 rounded-full mr-1"
                                        style={{ backgroundColor: algoColor(d.algo) }}
                                    />
                                    {d.algo}
                                </td>
                                <td className="py-2 px-3 font-mono">{d.interfaceOut}</td>
                                <td className={`py-2 px-3 text-right font-semibold ${mbpsColor(d.mbps)}`}>
                                    {d.mbps.toFixed(1)}
                                </td>
                                <td className="py-2 px-3 text-right">{d.gbps.toFixed(3)}</td>
                                <td className="py-2 px-3 text-right">{d.pps.toLocaleString()}</td>
                                <td className="py-2 px-3 text-center">
                                    {onViewPath ? (
                                        <button
                                            onClick={() => onViewPath(d.source, d.dest)}
                                            className="text-semantic-text-brand hover:underline
                                                bg-transparent border-none cursor-pointer p-0 text-xs"
                                        >
                                            View in topo
                                        </button>
                                    ) : (
                                        <a
                                            href={d.topoLink}
                                            className="text-semantic-text-brand hover:underline"
                                        >
                                            View in topo
                                        </a>
                                    )}
                                </td>
                            </tr>
                            );
                        })}
                        {filtered.length === 0 && (
                            <tr>
                                <td colSpan={8} className="py-8 text-center text-semantic-text-secondary">
                                    No demand data available
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default Srv6DemandMatrixTab;
